import React, { useState, useEffect } from "react";
import { Redirect, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Heading, Group, Input, Form } from "@peracto/peracto-ui";
import { faArrowCircleLeft } from "@fortawesome/pro-regular-svg-icons/faArrowCircleLeft";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GET_ONE, useClient } from "@peracto/client";
import { toast } from "react-toastify";

interface ResolvedTransfer {
  iri: string;
  basketId: string;
  createdAt: string;
}

interface BoschSession {
  "@id": string;
  id: number;
  user: string;
  createdAt: string;
  orderScreenSessionId: string;
  transferUrl: string | null;
  targetPath: string | null;
  vin: string | null;
  tecDocKTypeId: string | null;
  tecDocNTypeId: string | null;
  osVehicleKey: string | null;
  licensePlate: string | null;
  transfers: (string | { "@id": string })[];
}

const ViewSessionContainer = ({ children }) => {
  return (
    <div className="">
      <Heading name="View Bosch Session">
        <div className="flex-grow-1 d-flex align-items-center justify-content-end">
          <Link className="btn btn-outline-primary" to="/bosch-session-logs">
            <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
            Back to Sessions
          </Link>
        </div>
      </Heading>
      {children}
    </div>
  );
};

const SessionView = ({
  match: {
    params: { id },
  },
}) => {
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState<BoschSession | null>(null);
  const [resolvedTransfers, setResolvedTransfers] = useState<ResolvedTransfer[]>([]);
  const [redirect, setRedirect] = useState<string>();

  const { client } = useClient();

  const fetchSession = async () => {
    setLoading(true);
    try {
      const { data } = await client(GET_ONE, "bosch-session-logs", {
        id: `/bosch-session-logs/${id}`,
      });

      setSessionData(data);

      if (data.transfers && data.transfers.length > 0) {
        const fetched: ResolvedTransfer[] = await Promise.all(
          data.transfers.map(async (ref): Promise<ResolvedTransfer> => {
            const iri = typeof ref === "string" ? ref : ref["@id"];
            try {
              const { data: t } = await client(GET_ONE, "bosch-transfers", {
                id: iri,
              });
              return {
                iri,
                basketId: t.basketId || "",
                createdAt: t.createdAt || "",
              };
            } catch {
              return { iri, basketId: "", createdAt: "" };
            }
          })
        );
        setResolvedTransfers(fetched);
      }

      setLoading(false);
    } catch (e) {
      console.error(e);
      toast.error("There was an error loading this session.");
      setLoading(false);
      setRedirect("/bosch-session-logs");
    }
  };

  useEffect(() => {
    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <ViewSessionContainer>
        <div className="card">
          <div className="card-body">Loading...</div>
        </div>
      </ViewSessionContainer>
    );
  }

  return (
    <>
      <ViewSessionContainer>
        {redirect ? (
          <Redirect to={redirect} />
        ) : (
          <>
            <Helmet>
              <title>
                Bosch Session{" "}
                {sessionData?.orderScreenSessionId
                  ? `- ${sessionData.orderScreenSessionId}`
                  : ""}{" "}
                | Peracto
              </title>
            </Helmet>
            <Form
              onSubmit={() => {}}
              autoComplete="off"
              values={{
                orderScreenSessionId:
                  sessionData?.orderScreenSessionId || "",
                createdAt: sessionData?.createdAt || "",
                transferUrl: sessionData?.transferUrl || "",
                targetPath: sessionData?.targetPath || "",
                licensePlate: sessionData?.licensePlate || "",
                vin: sessionData?.vin || "",
                tecDocKTypeId: sessionData?.tecDocKTypeId || "",
                tecDocNTypeId: sessionData?.tecDocNTypeId || "",
                osVehicleKey: sessionData?.osVehicleKey || "",
              }}
            >
              <Group
                key="sessionDetails"
                id="sessionDetails"
                name="Session Details"
              >
                <Input
                  name="orderScreenSessionId"
                  label="Order Screen Session ID"
                  disabled
                  testId="orderScreenSessionId"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="createdAt"
                  label="Created At"
                  disabled
                  testId="createdAt"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="transferUrl"
                  label="Transfer URL"
                  disabled
                  testId="transferUrl"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="targetPath"
                  label="Target Path"
                  disabled
                  testId="targetPath"
                  className="hover:cursor-not-allowed"
                />
              </Group>

              <Group
                key="vehicleDetails"
                id="vehicleDetails"
                name="Vehicle Details"
              >
                <Input
                  name="licensePlate"
                  label="License Plate"
                  disabled
                  testId="licensePlate"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="vin"
                  label="VIN"
                  disabled
                  testId="vin"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="tecDocKTypeId"
                  label="TecDoc KType ID"
                  disabled
                  testId="tecDocKTypeId"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="tecDocNTypeId"
                  label="TecDoc NType ID"
                  disabled
                  testId="tecDocNTypeId"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="osVehicleKey"
                  label="OS Vehicle Key"
                  disabled
                  testId="osVehicleKey"
                  className="hover:cursor-not-allowed"
                />
              </Group>
            </Form>

            {resolvedTransfers.length > 0 && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Transfers</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped mb-0">
                      <thead>
                        <tr>
                          <th>Basket ID</th>
                          <th>Created</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resolvedTransfers.map((transfer, index) => (
                          <tr key={transfer.iri || index}>
                            <td>{transfer.basketId}</td>
                            <td>{transfer.createdAt}</td>
                            <td>
                              <Link to={transfer.iri}>
                                <button className="mr-2 btn btn-sm btn-primary">
                                  View
                                </button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </ViewSessionContainer>
    </>
  );
};

export default SessionView;
